import { baseApi } from 'services/api';
import { API_TAG_TYPES, API_VERSION } from 'constants/common';
import {
  CreateExternalCompanyPayload,
  CreateExternalCompanyResponse,
  DeleteExternalCompanyResponse,
  GetExternalCompaniesParams,
  GetExternalCompaniesResponse,
  GetExternalCompanyByIdResponse,
  UpdateExternalCompanyPayload,
  UpdateExternalCompanyResponse,
  UpdateExternalCompanyStatusPayload,
  UpdateExternalCompanyStatusResponse
} from './externalCompany.model';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotifier } from 'containers/app/appSlice';

const ExternalCompanyApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getExternalCompanies: builder.query<GetExternalCompaniesResponse, GetExternalCompaniesParams>({
      query: ({ teamId, status }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/external-company`,
        params: {
          status
        },
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.EXTERNAL_COMPANY],
      extraOptions: {
        failure: 'Failed to fetch external companies',
        showToast: true
      }
    }),
    updateExternalCompanyStatus: builder.mutation<
      UpdateExternalCompanyStatusResponse,
      UpdateExternalCompanyStatusPayload
    >({
      query: ({ teamId, externalCompanyId, status }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/external-company/${externalCompanyId}/status`,
        method: 'PATCH',
        body: { status }
      }),
      async onQueryStarted({ teamId, externalCompanyId, status }, { dispatch, queryFulfilled }) {
        // Optimistically update the getExternalCompanies query data
        const patchResult = dispatch(
          ExternalCompanyApi.util.updateQueryData('getExternalCompanies', { teamId, status }, draft => {
            const company = draft.data.find(c => c.id === externalCompanyId);
            if (company) {
              company.status = status;
            }
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // If the mutation fails, revert the optimistic update
          patchResult.undo();
        }
      },
      invalidatesTags: ['ExternalCompany'],
      extraOptions: {
        failure: 'Failed to update external company status',
        showToast: true
      }
    }),
    deleteExternalCompany: builder.mutation<
      DeleteExternalCompanyResponse,
      { teamId: string; externalCompanyId: string }
    >({
      query: ({ teamId, externalCompanyId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/external-company/${externalCompanyId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ExternalCompany'],
      extraOptions: {
        failure: 'Failed to delete external company',
        success: 'External company deleted successfully',
        showToast: true
      }
    }),
    createExternalCompany: builder.mutation<CreateExternalCompanyResponse, CreateExternalCompanyPayload>({
      query: ({ teamId, ...payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/external-company`,
        method: 'POST',
        body: payload
      }),
      async onQueryStarted(payload, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(
            showNotifier({
              message: { primaryMessage: `${payload.companyName} has been added successfully` },
              type: NotifierTypes.SUCCESS,
              showClose: true
            })
          );
        } catch (error) {
          // Error handling is managed by extraOptions
        }
      },
      invalidatesTags: [API_TAG_TYPES.EXTERNAL_COMPANY],
      extraOptions: {
        failure: 'Failed to create external company',
        showToast: true
      }
    }),
    updateExternalCompany: builder.mutation<UpdateExternalCompanyResponse, UpdateExternalCompanyPayload>({
      query: ({ teamId, externalCompanyId, ...payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/external-company/${externalCompanyId}`,
        method: 'PUT',
        body: payload
      }),
      async onQueryStarted(payload, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(
            showNotifier({
              message: { primaryMessage: `${payload.companyName} has been saved successfully` },
              type: NotifierTypes.SUCCESS,
              showClose: true
            })
          );
        } catch (error) {
          // Error handling is managed by extraOptions
        }
      },
      extraOptions: {
        failure: 'Failed to update external company',
        showToast: true
      }
    }),
    getExternalCompanyById: builder.query<
      GetExternalCompanyByIdResponse,
      { teamId: string; externalCompanyId: string }
    >({
      query: ({ teamId, externalCompanyId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/external-company/${externalCompanyId}`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch external company details',
        showToast: true
      }
    })
  })
});

export const {
  useGetExternalCompaniesQuery,
  useUpdateExternalCompanyStatusMutation,
  useDeleteExternalCompanyMutation,
  useCreateExternalCompanyMutation,
  useUpdateExternalCompanyMutation,
  useLazyGetExternalCompanyByIdQuery,
  useLazyGetExternalCompaniesQuery
} = ExternalCompanyApi;
